import React from 'react';
import {
  Dropdown,
  Icon,
  Grid,
  Modal,
  ModalHeader,
  ModalContent,
  ModalDescription,
  ModalActions,
  Button
} from 'semantic-ui-react';
import { iRootState } from '../../store';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { cleanUserSession } from '../../services/auth';
import {
  AvailableBrokersAndEstatesState,
  CurrentPageState
} from '../../store/types/temp-types';
import { UserState } from '../../types/UserState';
import UnifiedApps from '../../components/UnifiedApps';
import { get } from '../../services/request';
import {
  Avatar,
  CustomDropdownItemContainer,
  DropdownContainer,
  Logo,
  MobileHeaderContainer,
  NavbarContainer,
  NavbarHeaderGroup,
  NavbarText,
  UserContainer,
  UserDropdownWrapper,
  DescriptionText,
  UserInfoContainer,
  UserName,
  ContentText,
  IconWrapper,
  EstateSwitcherContainer,
  EstateSwitcherTrigger,
  EstateTriggerMain,
  EstateTriggerTextGroup,
  EstateNameText,
  EstateCityText,
  EstateUnitCard,
  EstateUnitLabel,
  EstateUnitName,
  EstateSwitcherPanel,
  EstatePanelHeader,
  EstatePanelTitle,
  EstateSelectedCard,
  EstateSelectedBadge,
  EstatePanelBody,
  EstateSearchInput,
  EstateList,
  EstateListItem
} from './styles';
import IconButton from '../../components/IconButton';
import DadosCadastraisModal from './DadosCadastraisModal';
import useWindowSize from '../../hooks/useWindowSize';
import logocfx from '../../resources/imgs/newlogo.png';
import logorv from '../../resources/imgs/logo-lado-bom-seguros.svg';
import { DateToStringDateTime } from '../../services/date';
import { translateUserRole } from '../../util';
import * as Storage from '../../services/storage';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { resolveUserEstateUnitName } from '../../utils/userEstateUnit.util';

const mapState = (state: iRootState) => ({
  user: state.user,
  currentPage: state.currentPage,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

const mapDispatch = (dispatch: any) => ({
  updateUser: (user: Partial<UserState>) => dispatch.user.updateUser(user),
  updateAvailableBrokersAndEstates: (
    payload: Partial<AvailableBrokersAndEstatesState>
  ) => dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(payload)
});

export async function invalidateCache() {
  try {
    await get('/users/invalidate-cache');
  } catch (error) {
    console.error('Failed to invalidate cache:', error);
  } finally {
    cleanUserSession();
  }
}

export type NavbarProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
  RouteComponentProps & {
    handleToggleSidebar: () => void;
    isCollapsed?: boolean;
  };

const Navbar = (props: NavbarProps) => {
  const [openModalMaterialApoio, setOpenModalMaterialApoio] = React.useState(false);
  const [openDadosCadastrais, setOpenDadosCadastrais] = React.useState(false);
  const [isEstateSwitcherOpen, setIsEstateSwitcherOpen] = React.useState(false);
  const [estateSearchTerm, setEstateSearchTerm] = React.useState('');
  const estateSwitcherRef = React.useRef<HTMLDivElement>(null);
  const currentPage: CurrentPageState = props.currentPage;
  const user: UserState = props.user;
  const availableBrokersAndEstates = props.availableBrokersAndEstates;

  const isRV = process.env.REACT_APP_IS_RV === 'true';
  //const logo = isRV ? logorv : logocfx;
  const logo = logorv;

  const { isMobile } = useWindowSize();
  const dropdownMenuStyles = {
    borderTop: '8px solid #59249B',
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7
  };

  const logoutHandler = async () => {
    await invalidateCache();
  };

  const accessibleEstatesMap = React.useMemo(() => {
    const entries: Array<[string, any]> = (availableBrokersAndEstates?.accessibleEstates || []).map(
      estate => [String(estate.id), estate]
    );
    return new Map<string, any>(entries);
  }, [availableBrokersAndEstates?.accessibleEstates]);

  const estateSwitcherOptions = React.useMemo(
    () =>
      (availableBrokersAndEstates?.estates || []).map((estateOption: any) => {
        const optionValue = estateOption?.value;
        const optionData = estateOption?.data;
        const rawEstate: any =
          typeof optionValue === 'object' && optionValue !== null
            ? optionValue
            : typeof optionData === 'object' && optionData !== null
              ? optionData
              : {};
        const estateId = String(rawEstate?.id || optionData?.id || optionValue || estateOption?.key || '');
        const fallbackEstate: any = accessibleEstatesMap.get(estateId);
        const estateName = String(
          rawEstate?.name || optionData?.name || fallbackEstate?.name || estateOption?.text || ''
        );
        const estateCity = String(
          rawEstate?.estateCity ||
          rawEstate?.estate_city ||
          rawEstate?.city ||
          optionData?.estateCity ||
          optionData?.estate_city ||
          optionData?.city ||
          fallbackEstate?.estateCity ||
          fallbackEstate?.city ||
          ''
        );
        const estateState = String(
          rawEstate?.estateState ||
          rawEstate?.estate_state ||
          rawEstate?.state ||
          rawEstate?.uf ||
          optionData?.estateState ||
          optionData?.estate_state ||
          optionData?.state ||
          optionData?.uf ||
          fallbackEstate?.estateState ||
          fallbackEstate?.state ||
          fallbackEstate?.uf ||
          ''
        );
        const normalizedEstateData = {
          ...(fallbackEstate || {}),
          ...(typeof optionData === 'object' && optionData !== null ? optionData : {}),
          ...rawEstate,
          id: estateId,
          name: estateName,
          estateCity,
          estateState
        };

        return {
          key: estateId,
          text: estateName,
          value: estateId,
          city: estateCity,
          state: estateState,
          data: normalizedEstateData
        };
      }),
    [availableBrokersAndEstates?.estates, accessibleEstatesMap]
  );

  const activeEstateId =
    availableBrokersAndEstates?.activeEstateId || user?.activeEstateId || user?.estate?.id;
  const isEstateRole =
    user?.role === UserRolesEnum.estateAdmin || user?.role === UserRolesEnum.estateUser;
  const canSwitchEstate = isEstateRole && estateSwitcherOptions.length > 1;
  const estateUnitName = resolveUserEstateUnitName(user);
  const showEstateUnit = Boolean(estateUnitName);

  const selectedEstate =
    estateSwitcherOptions.find(option => option.value === String(activeEstateId || '')) ||
    estateSwitcherOptions[0];

  const formatEstateLocation = (city?: string, state?: string) => {
    if (city && state) {
      return `${city} - ${state}`;
    }

    return city || state || '-';
  };

  const filteredEstateOptions = React.useMemo(() => {
    const normalizedSearch = estateSearchTerm.trim().toLowerCase();
    const optionsWithoutCurrent = estateSwitcherOptions.filter(
      option => option.value !== selectedEstate?.value
    );

    if (!normalizedSearch) {
      return optionsWithoutCurrent;
    }

    return optionsWithoutCurrent.filter(option => {
      const searchable = `${option.text} ${option.city} ${option.state}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [estateSearchTerm, estateSwitcherOptions, selectedEstate?.value]);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        isEstateSwitcherOpen &&
        estateSwitcherRef.current &&
        !estateSwitcherRef.current.contains(event.target as Node)
      ) {
        setIsEstateSwitcherOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isEstateSwitcherOpen]);

  const handleEstateSwitch = (estateId: string) => {
    const estateToSelect = estateSwitcherOptions.find(option => option.value === estateId);
    if (!estateToSelect) {
      return;
    }

    Storage.set('active_estate_id', String(estateId));
    Storage.set('estate', JSON.stringify(estateToSelect.data || { id: estateId }));

    props.updateAvailableBrokersAndEstates({ activeEstateId: String(estateId) });
    props.updateUser({
      activeEstateId: String(estateId),
      estate: estateToSelect.data || user?.estate
    });
  };

  const handleEstateSwitcherToggle = () => {
    setIsEstateSwitcherOpen(previousState => !previousState);
  };

  const handleEstateSelection = (estateId: string) => {
    handleEstateSwitch(estateId);
    setEstateSearchTerm('');
    setIsEstateSwitcherOpen(false);
  };

  const getInitials = (fullName: string): string => {
    const nameParts = fullName.split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    } else if (nameParts.length > 1) {
      const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
      const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return `${firstNameInitial}${lastNameInitial}`;
    }
    return '';
  };

  const trigger = (
    <>
      <UserContainer>
        {isMobile ? (
          <Avatar flex={0.3} circular size="big">
            {getInitials(user?.name || user?.estate?.name || '')}
          </Avatar>
        ) : (
          <>
            <Icon
              name="user circle"
              size="large"
              style={{ color: '#854EE0', margin: 0, flexShrink: 0 }}
            />
            <UserInfoContainer>
              <UserName>{user.name}</UserName>
              <DescriptionText
                weight={400}
                style={{ fontSize: '12px', lineHeight: '12px', color: '#AAADB1' }}
              >
                {user.email}
              </DescriptionText>
            </UserInfoContainer>
            <Icon name="angle down" style={{ color: '#AAADB1', margin: 0, flexShrink: 0 }} />
          </>
        )}
      </UserContainer>
    </>
  );

  const CustomHeader = (): JSX.Element => {
    // If the currentPage exists and has a title
    if (currentPage && currentPage.title) {
      // If the user is an estate and the current page is the proposals listing
      if (user && user.estate && currentPage.name === 'insurance[proposals]') {
        return (
          <Grid.Column style={{ flex: '0.98', paddingTop: '1em' }}>
            <NavbarText isCollapsed={props.isCollapsed}>
              {currentPage?.parent?.text && (
                <h4 className="parent-title">{currentPage.parent.text}</h4>
              )}

              <span>{currentPage.title}</span>
            </NavbarText>

            <p style={{ paddingLeft: '2em', paddingTop: '0.5em' }}>
              Dia de fechamento de lote: {user.estate.batchClosingDay}
            </p>
          </Grid.Column>
        );
      } else {
        return (
          <NavbarText isCollapsed={props.isCollapsed}>
            {currentPage?.parent?.text && (
              <h4 className="parent-title">{currentPage.parent.text}</h4>
            )}

            <span>{currentPage.title}</span>
          </NavbarText>
        );
      }
    }

    return <NavbarText isCollapsed={props.isCollapsed} />;
  };

  const ModalMaterialApoio = () => {
    const urlMaterial =
      'https://4seg-documents-analysis.s3.us-east-1.amazonaws.com/Geral/RV+SEGUROS+SLA.pdf';
    const urlRegulamentoPottencial =
      'https://4seg-documents-analysis.s3.us-east-1.amazonaws.com/Geral/Contrato+-+Pottencializou+Ganhou+-+Agosto+a+Dezembro+2025+(1).pdf';
    const urlGuiaResgateAlyPlus =
      'https://4seg-documents-analysis.s3.us-east-1.amazonaws.com/Geral/AlyPlus_Guia+de+Resgate+de+Bo%CC%82nus+-+Baixa+(1).pdf';

    const documentLinkStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      marginBottom: '12px',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      border: '1px solid #e0e0e0'
    };

    const documentLinkHoverStyle = {
      backgroundColor: '#f0f0f1',
      transform: 'translateX(5px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    };

    const iconContainerStyle = {
      minWidth: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    const linkTextStyle = {
      color: '#59249B',
      fontWeight: 500,
      fontSize: '14px',
      textDecoration: 'none',
      flex: 1
    };

    const sectionTitleStyle = {
      fontSize: '13px',
      fontWeight: 600,
      color: '#666',
      textTransform: 'uppercase' as const,
      marginTop: '20px',
      marginBottom: '12px',
      letterSpacing: '0.5px'
    };

    return (
      <Modal
        onClose={() => setOpenModalMaterialApoio(false)}
        onOpen={() => setOpenModalMaterialApoio(true)}
        open={openModalMaterialApoio}
        size="small"
      >
        <ModalHeader style={{ borderBottom: '3px solid #59249B' }}>
          Material de apoio
        </ModalHeader>
        <ModalContent style={{ padding: '25px' }}>
          <ModalDescription>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Acesse a documentação de suporte para utilização da plataforma.
            </p>

            <div style={sectionTitleStyle}>📋 Documentos Gerais</div>
            <div
              style={documentLinkStyle}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, documentLinkHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, documentLinkStyle);
              }}
            >
              <div style={iconContainerStyle}>
                <Icon name="file alternate outline" size="large" color="grey" />
              </div>
              <a
                href={urlMaterial}
                target="_blank"
                rel="noopener noreferrer"
                style={linkTextStyle}
              >
                SLA de Atendimento para Imobiliárias Parceiras
              </a>
              <Icon name="external alternate" size="small" color="grey" />
            </div>

            <div style={sectionTitleStyle}>🏆 Campanha Pottencializou Ganhou</div>
            <div
              style={documentLinkStyle}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, documentLinkHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, documentLinkStyle);
              }}
            >
              <div style={iconContainerStyle}>
                <Icon name="trophy" size="large" color="purple" />
              </div>
              <a
                href={urlRegulamentoPottencial}
                target="_blank"
                rel="noopener noreferrer"
                style={linkTextStyle}
              >
                Regulamento da Campanha
              </a>
              <Icon name="external alternate" size="small" color="purple" />
            </div>

            <div
              style={documentLinkStyle}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, documentLinkHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, documentLinkStyle);
              }}
            >
              <div style={iconContainerStyle}>
                <Icon name="gift" size="large" color="purple" />
              </div>
              <a
                href={urlGuiaResgateAlyPlus}
                target="_blank"
                rel="noopener noreferrer"
                style={linkTextStyle}
              >
                Guia para Resgate de Bônus
              </a>
              <Icon name="external alternate" size="small" color="purple" />
            </div>
          </ModalDescription>
        </ModalContent>
        <ModalActions style={{ backgroundColor: '#f8f9fa' }}>
          <Button color="grey" onClick={() => setOpenModalMaterialApoio(false)}>
            Fechar
          </Button>
        </ModalActions>
      </Modal>
    );
  };

  const renderDropdownMenu = () => {
    return (
      <Dropdown.Menu style={dropdownMenuStyles}>
        <CustomDropdownItemContainer hasBottomBorder disabled>
          <section>
            <UserName>{user?.name || user.estate?.name}</UserName>
            <DescriptionText weight={400}>
              Permissão/{translateUserRole(user.role)}
            </DescriptionText>
          </section>
          <section>
            <DescriptionText weight={400}>Contato</DescriptionText>
            <ContentText>{user.email}</ContentText>
          </section>
          {(user.estate?.name || user.franchise?.name) && (
            <section>
              <DescriptionText weight={400}>Filiação</DescriptionText>
              <ContentText>{user.estate?.name || user.franchise?.name}</ContentText>
            </section>
          )}
          {showEstateUnit && (
            <section>
              <DescriptionText weight={400}>Unidade</DescriptionText>
              <ContentText>{estateUnitName}</ContentText>
            </section>
          )}
          {user?.lastLoginAt && (
            <section>
              <DescriptionText weight={400}>
                Último acesso: {DateToStringDateTime(user?.lastLoginAt)}
              </DescriptionText>
            </section>
          )}
        </CustomDropdownItemContainer>
        {isEstateRole && (
          <CustomDropdownItemContainer
            align="row"
            text="Dados cadastrais"
            icon="file alternate outline"
            hasBottomBorder
            onClick={() => setOpenDadosCadastrais(true)}
          />
        )}

        <CustomDropdownItemContainer
          align="row"
          text="Material de apoio"
          icon="question circle outline"
          hasBottomBorder
          onClick={() => setOpenModalMaterialApoio(true)}
        />
        <CustomDropdownItemContainer
          align="row"
          text="Sair"
          icon="sign-out"
          onClick={logoutHandler}
        />
      </Dropdown.Menu>
    );
  };

  const MobileHeader = (): JSX.Element => {
    return (
      <MobileHeaderContainer>
        <IconButton name="bars" size="large" onClick={props.handleToggleSidebar} />
        <Logo src={logo} />
        <Dropdown trigger={trigger} icon={null} direction="left">
          {renderDropdownMenu()}
        </Dropdown>
      </MobileHeaderContainer>
    );
  };

  return (
    <>
      {!isMobile && (
        <NavbarContainer isCollapsed={props.isCollapsed}>
          <CustomHeader />
          <DropdownContainer>
            <NavbarHeaderGroup>
              {showEstateUnit && (
                <EstateUnitCard>
                  <Icon
                    name="map marker alternate"
                    size="large"
                    style={{ color: '#854EE0', margin: 0, flexShrink: 0 }}
                  />
                  <EstateTriggerTextGroup>
                    <EstateUnitLabel>Unidade</EstateUnitLabel>
                    <EstateUnitName title={estateUnitName}>{estateUnitName}</EstateUnitName>
                  </EstateTriggerTextGroup>
                </EstateUnitCard>
              )}
              {canSwitchEstate && selectedEstate && (
                <EstateSwitcherContainer ref={estateSwitcherRef}>
                  <EstateSwitcherTrigger
                    type="button"
                    isOpen={isEstateSwitcherOpen}
                    hasUnitBefore={showEstateUnit}
                    onClick={handleEstateSwitcherToggle}
                  >
                    <EstateTriggerMain>
                      <Icon
                        name="building outline"
                        size="large"
                        style={{ color: '#854EE0', margin: 0, flexShrink: 0 }}
                      />
                      <EstateTriggerTextGroup>
                        <EstateNameText>{selectedEstate.text}</EstateNameText>
                        <EstateCityText>
                          {formatEstateLocation(selectedEstate.city, selectedEstate.state)}
                        </EstateCityText>
                      </EstateTriggerTextGroup>
                    </EstateTriggerMain>
                    <Icon
                      name={isEstateSwitcherOpen ? 'angle up' : 'angle down'}
                      style={{ color: '#854EE0', margin: 0, flexShrink: 0 }}
                    />
                  </EstateSwitcherTrigger>

                  {isEstateSwitcherOpen && (
                    <EstateSwitcherPanel>
                      <EstatePanelHeader>
                        <EstatePanelTitle>Acessar imobiliária</EstatePanelTitle>
                      </EstatePanelHeader>

                      <EstateSelectedCard
                        type="button"
                        onClick={() => handleEstateSelection(selectedEstate.value)}
                      >
                        <EstateSelectedBadge>
                          <Icon name="check circle outline" size="small" style={{ margin: 0 }} />
                          Selecionada
                        </EstateSelectedBadge>

                        <EstateTriggerMain>
                          <Icon
                            name="building outline"
                            size="large"
                            style={{ color: '#C9ADFF', margin: 0, flexShrink: 0 }}
                          />
                          <EstateTriggerTextGroup>
                            <EstateNameText isSelected>{selectedEstate.text}</EstateNameText>
                            <EstateCityText isSelected>
                              {formatEstateLocation(selectedEstate.city, selectedEstate.state)}
                            </EstateCityText>
                          </EstateTriggerTextGroup>
                        </EstateTriggerMain>
                      </EstateSelectedCard>

                      <EstatePanelBody>
                        <EstateSearchInput
                          placeholder="Buscar"
                          value={estateSearchTerm}
                          onChange={event => setEstateSearchTerm(event.target.value)}
                        />

                        <EstateList>
                          {filteredEstateOptions.map(option => (
                            <EstateListItem
                              key={option.value}
                              type="button"
                              onClick={() => handleEstateSelection(option.value)}
                            >
                              <Icon
                                name="building outline"
                                size="large"
                                style={{ color: '#DDDDDD', margin: 0, flexShrink: 0 }}
                              />
                              <EstateTriggerTextGroup>
                                <EstateNameText>{option.text}</EstateNameText>
                                <EstateCityText>
                                  {formatEstateLocation(option.city, option.state)}
                                </EstateCityText>
                              </EstateTriggerTextGroup>
                            </EstateListItem>
                          ))}
                        </EstateList>
                      </EstatePanelBody>
                    </EstateSwitcherPanel>
                  )}
                </EstateSwitcherContainer>
              )}

              <UserDropdownWrapper isAlone={!canSwitchEstate && !showEstateUnit}>
                <Dropdown trigger={trigger} icon={null} direction="left">
                  {renderDropdownMenu()}
                </Dropdown>
              </UserDropdownWrapper>
            </NavbarHeaderGroup>

            {isRV && <UnifiedApps />}
          </DropdownContainer>
        </NavbarContainer>
      )}
      {isMobile && <MobileHeader />}
      <ModalMaterialApoio />
      <DadosCadastraisModal
        isOpen={openDadosCadastrais}
        onClose={() => setOpenDadosCadastrais(false)}
        estateId={String(user?.estate?.id || '')}
      />
    </>
  );
};

export default connect(mapState, mapDispatch)(Navbar);
